<script setup lang="ts">
import UnitLinkCard from '@/components/cards/UnitLinkCard.vue';
import type { Unit } from '@/typings/units/Unit.ts';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls.ts';
import { useI18n } from 'vue-i18n';
import type { LocaleSchema } from '@/locales/index.ts';
import { isUnitDisabled } from '@/enums/UnitStatus';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const selectedUnit = defineModel<Unit>('selected');
const hoveredUnit = defineModel<Unit>('hovered');
const { homeUrl, getRentalUnitWizardUrl } = useRentalLocalizedUrls();

const props = defineProps<{
  units: Unit[];
  loading?: boolean;
  isSelectable?: boolean;
  mode?: 'rental' | 'purchase';
}>();

const emit = defineEmits<{
  'unit-selected': [unit: Unit];
}>();

function onUnitClick(unit: Unit) {
  if (props.isSelectable) {
    selectedUnit.value = unit;
    emit('unit-selected', unit);
  }
}

function onUnitHover(unit: Unit | undefined) {
  if (props.isSelectable && unit) {
    hoveredUnit.value = unit;
  } else if (!unit) {
    hoveredUnit.value = undefined;
  }
}
</script>

<template>
  <div class="unitList">
    <div v-if="loading" class="unitList-loadingState">
      <div class="unitList-skeleton preferredColumnCountGrid">
        <div v-for="i in 3" :key="i" class="unitList-skeletonCard">
          <div class="skeleton-content">
            <div class="skeleton-image"></div>
            <div class="skeleton-text-lines">
              <div class="skeleton-line skeleton-line--title"></div>
              <div class="skeleton-line skeleton-line--subtitle"></div>
              <div class="skeleton-line skeleton-line--description"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="units.length > 0" 
         class="unitList-unitGrid preferredColumnCountGrid">
      <UnitLinkCard v-for="unit in units"
                    :key="unit.id"
                    :mode="props.mode"
                    class="unitLinkList-unitCard"
                    :class="{
                      'unitLinkList-unitCard--selected': isSelectable && selectedUnit?.id === unit.id,
                      'unitLinkList-unitCard--hovered': isSelectable && hoveredUnit?.id === unit.id,
                      'unitLinkList-unitCard--selectable': isSelectable
                    }"
                    :unit="unit"
                    :href="!isSelectable ? getRentalUnitWizardUrl(unit) : ''"
                    @click="isSelectable ? onUnitClick(unit) : undefined"
                    @mouseenter="isSelectable ? onUnitHover(unit) : undefined"
                    @mouseleave="isSelectable ? onUnitHover(undefined) : undefined">
        <template #extra-info>
          <slot name="extra-info"
                :unit="unit">
          </slot>
        </template>
        <template #footer>
          <slot name="footer"
                :unit="unit">
          </slot>
        </template>
      </UnitLinkCard>
    </div>
    <div v-else
         class="unitList-emptyState">
      <h3>{{ t('browsingApp.units.noUnitsAvailable') }}</h3>
    </div>
  </div>
</template>

<style lang="scss">
.unitList {
  &-unitGrid, &-skeleton {
    --column-count: 2;
    --column-gap: var(--spc24-16);
    overflow: visible;
  }

  &-skeletonCard {
    background: var(--gray-50);
    border-radius: 8px;
    border: 1px solid var(--gray-200);
    overflow: hidden;
    
    .skeleton-content {
      padding: 0;
      
      .skeleton-image {
        height: 200px;
        background: linear-gradient(90deg, var(--gray-200) 25%, var(--gray-100) 50%, var(--gray-200) 75%);
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
      }
      
      .skeleton-text-lines {
        padding: 1rem;
        
        .skeleton-line {
          height: 12px;
          background: linear-gradient(90deg, var(--gray-200) 25%, var(--gray-100) 50%, var(--gray-200) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
          border-radius: 4px;
          margin-bottom: 8px;
          
          &--title {
            height: 16px;
            width: 75%;
          }
          
          &--subtitle {
            width: 50%;
          }
          
          &--description {
            width: 90%;
            margin-bottom: 0;
          }
        }
      }
    }
  }

  &-emptyState {
    text-align: center;
    padding: 2rem;
    color: var(--gray-500);

    h3 {
      margin: 0;
      font-weight: 500;
      font-style: italic;
    }
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Selectable unit card styles */
.unitLinkList-unitCard {
  &--selectable {
    cursor: pointer;
    
    a {
      pointer-events: none;
    }
    
    .cardTopImage_unitLinkCard {
      transition: all 0.2s ease;
    }
  }
  
  &--disabled {
    opacity: 0.6;
  }
  
  &--hovered:not(&--selected):not(&--disabled) .cardTopImage_unitLinkCard {
    outline-color: var(--primary-300);
    outline-offset: -2px;
    transform: translateY(-2px);
  }
  
  &--selected:not(&--disabled) .cardTopImage_unitLinkCard {
    outline: 3px solid var(--primary-500);
    outline-offset: -3px;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}
</style>
